import { api, unwrap, type APIResponse } from "./client";

export interface RecordingPart {
  kind: "merged" | "consult";
  bytes: number;
  /** null when the file could not be read — e.g. a call still in progress. */
  duration_seconds: number | null;
}

export interface CallRecording {
  session_id: string;
  started_at: string | null;
  started_local: string | null;
  agent: string | null;
  buyer: string | null;
  campaign_id: number | null;
  spool_id: number | null;
  outcome: string | null;
  merged: boolean;
  parts: RecordingPart[];
}

export interface CampaignLongCalls {
  campaign_id: number | null;
  campaign_name: string | null;
  recordings: number;
  over_180s: number;
}

export interface CallRecordingsResponse {
  items: CallRecording[];
  /** Every buyer this account has transferred to — not just those in the
   *  current result, so picking one never empties the menu. */
  buyers: string[];
  filtered_by: {
    buyer: string | null;
    min_seconds: number | null;
    date_from: string | null;
    date_to: string | null;
  };
  /** How many calls match buyer + threshold + period — the headline answer.
   *  Counted from the same durations the rows show, so the number and the
   *  list can never disagree. */
  matching_count: number;
  timezone: string;
  today: string;
  /** The threshold actually counted against: whatever was typed, else 180. */
  long_call_seconds: number;
  /** Recordings per campaign for the chosen period, and how many ran long. */
  by_campaign: CampaignLongCalls[];
}

export async function listCallRecordings(params?: {
  date_from?: string;
  date_to?: string;
  buyer?: string;
  min_seconds?: number;
}): Promise<CallRecordingsResponse> {
  const resp = await api.get<APIResponse<CallRecordingsResponse>>(
    "/transfers/recordings",
    { params },
  );
  return unwrap(resp.data);
}

/** Fetch the audio as a blob.
 *
 * NOT a plain URL on an <audio src>: the route is Bearer-authenticated and a
 * media element cannot send an Authorization header, so a bare src would 401.
 * axios attaches the token, and the caller turns the blob into an object URL.
 * Transfer recordings are minutes long, so holding one in memory is cheap —
 * the caller is responsible for revoking the URL when it swaps or unmounts.
 */
export async function fetchRecordingBlob(
  sessionId: string,
  kind: "merged" | "consult",
): Promise<Blob> {
  const resp = await api.get<Blob>(
    `/transfers/recordings/${sessionId}/${kind}`,
    { responseType: "blob" },
  );
  return resp.data;
}

/** Save one to disk, same auth path as playback. */
export async function downloadRecording(
  sessionId: string,
  kind: "merged" | "consult",
): Promise<void> {
  const blob = await fetchRecordingBlob(sessionId, kind);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `transfer-${sessionId}-${kind}.wav`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
